import { HeatmapLayer } from 'deck.gl';
import { useCallback, useEffect, useState } from 'react';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { GoogleMap } from '@react-google-maps/api';
import { Location } from 'types';
import { getLocationsHeatMapValue } from 'utils/map';

type HeatMapProps = {
  mapCenter: { lat: number; lng: number };
  mapZoom: number;
  locations: Location[];
  heatMapFilter: string;
};

type HeatmapPoint = {
  position: [number, number];
  weight: number;
};

export default function HeatMap({
  mapCenter,
  mapZoom,
  locations,
  heatMapFilter
}: HeatMapProps) {
  const [overlay] = useState(() => new GoogleMapsOverlay({}));

  useEffect(() => {
    return () => {
      overlay.setMap(null);
      overlay.finalize();
    };
  }, [overlay]);

  const getHeatMapWeight = useCallback(
    (heatMapFilter: string) =>
      getLocationsHeatMapValue(locations, heatMapFilter),
    [locations]
  );

  useEffect(() => {
    if (heatMapFilter) {
      const weights = getHeatMapWeight(heatMapFilter);
      const heatmapData: HeatmapPoint[] = locations
        .map((location, i) => {
          const weight = weights[i];

          if (weight) {
            return {
              position: [location.longitude, location.latitude] as [
                number,
                number
              ],
              weight
            };
          }
          return null;
        })
        .filter((item): item is HeatmapPoint => item !== null);

      overlay.setProps({
        layers: [
          new HeatmapLayer({
            id: 'heatmap-layer',
            data: heatmapData,
            getPosition: (d: HeatmapPoint) => d.position,
            getWeight: (d: HeatmapPoint) => d.weight,
            radiusPixels: 50
          })
        ]
      });
    } else {
      overlay.setProps({ layers: [] });
    }
  }, [overlay, heatMapFilter, locations, getHeatMapWeight]);

  return (
    <div className="absolute top-0 right-0 bottom-0 left-0 hidden sm:block h-full w-full">
      <GoogleMap
        mapContainerClassName="map-container"
        center={mapCenter}
        zoom={mapZoom}
        options={{
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: false,
          zoomControlOptions: {
            position: 3
          }
        }}
        id="act-dashboard-google-map-heat"
        onLoad={(map) => overlay.setMap(map)}
      />
    </div>
  );
}
